import React, { useEffect, useState } from "react";
import { Collapse, Button, Input, InputNumber, Select, Tag, message, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faFileCode, faDownload } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
// INTERFACES
import {
  AdapterBacnetInterface,
  BacnetBankMapping,
  BacnetLn2Mapping,
  BacnetUploadFormat,
} from "../../../interfaces/Adapter.interface";
import { RootState } from "../../../app/store";
// COMPONENTS
import FlatbufferMessagePicker from "../../components/select/FlatbufferMessagePicker";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
// INTERFACES
import { FbMessageRef } from "../../../interfaces/FbMessage.interface";
// UTILS
import { stringToUuidT, uuidToString } from "../../../utils/uuid";
import {
  serializeMappings,
  deserializeMappings,
} from "../../edgeManagement/adapters/CDO/BACNET/CdoBacnetPage";
import {
  parseCompositeKey,
  LN2_MESSAGE_CATEGORIES,
  categoryKeyToMessageContent,
} from "../../../flatbuffers/types/ln2MessageOptions";
// FLATBUFFERS
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";

/**
 * Finds the first value in an object that looks like a FlatBuffer union envelope
 * { Type: number|string, Value: any }. Used to navigate VTS FlatBuffer JSON generically.
 * Handles both numeric Type (standard flatc Go codegen) and string Type (some codegen variants).
 */
function firstUnionField(obj: any): { Type: number | string; Value: any } | null {
  if (!obj || typeof obj !== "object") return null;
  const key = Object.keys(obj).find((k) => {
    const val = obj[k];
    return (
      val !== null &&
      typeof val === "object" &&
      (typeof val.Type === "number" || typeof val.Type === "string") &&
      "Value" in val
    );
  });
  return key !== undefined ? (obj[key] as { Type: number | string; Value: any }) : null;
}

/**
 * Resolve a union Type to a numeric MessageContent value. Go FlatBuffer codegen
 * serializes union discriminants as numeric byte values, but we handle string
 * enum names as a defensive fallback (e.g., "FaultMessage" → 6).
 *
 * Derived from the generated MessageContent enum — not hand-rolled.
 */
const GO_ENUM_NAME_TO_MC: ReadonlyMap<string, MessageContent> = new Map(
  Object.entries(MessageContent)
    .filter(
      (entry): entry is [string, MessageContent] => typeof entry[1] === "number" && entry[1] !== 0,
    )
    .map(([key, val]) => {
      // Generated keys like "Fault_FaultMessage" → Go uses "FaultMessage"
      const suffix = key.includes("_") ? key.split("_").slice(1).join("_") : key;
      return [suffix, val];
    }),
);
function resolveUnionType(t: number | string): number {
  if (typeof t === "number") return t;
  return GO_ENUM_NAME_TO_MC.get(t) ?? MessageContent.NONE;
}

/**
 * Recursively walk nested FlatBuffer union envelopes ({Type, Value}) until we
 * reach a terminal table whose only non-Type/Value field is a scalar `message`
 * (the enum integer). Returns the variant ("normal" or "valued") and the enum value.
 *
 * The variant is determined from the penultimate union Type — in FlatBuffer
 * schemas, Normal* variants are Type=1 and Valued* variants are Type=2.
 */
interface LeafResult {
  variantType: number | string | null;
  enumValue: number;
}
function walkUnionToLeafInternal(obj: any, maxDepth: number): LeafResult | null {
  if (!obj || typeof obj !== "object" || maxDepth <= 0) return null;

  if (typeof obj.message === "number") {
    return { variantType: null, enumValue: obj.message };
  }

  const union = firstUnionField(obj);
  if (union) {
    const child = walkUnionToLeafInternal(union.Value, maxDepth - 1);
    if (child) {
      return {
        variantType: child.variantType ?? union.Type,
        enumValue: child.enumValue,
      };
    }
  }

  return null;
}
function walkUnionToLeaf(
  obj: any,
  maxDepth = 6,
): { variant: "normal" | "valued"; enumValue: number } | null {
  const result = walkUnionToLeafInternal(obj, maxDepth);
  if (!result) return null;
  let isValued = false;
  if (typeof result.variantType === "number") {
    isValued = result.variantType === 2;
  } else if (typeof result.variantType === "string") {
    isValued = result.variantType.startsWith("Valued");
  }
  return {
    variant: isValued ? "valued" : "normal",
    enumValue: result.enumValue,
  };
}

/**
 * Convert a composite key string (e.g. "status:normal:7") to an FbMessageRef.
 * Used when hydrating from the simplified upload JSON format.
 */
function compositeKeyToMsgRef(key: string): FbMessageRef | null {
  const parsed = parseCompositeKey(key);
  if (!parsed) return null;
  const category = LN2_MESSAGE_CATEGORIES.find((c) => c.categoryKey === parsed.categoryKey);
  if (!category) return null;
  const option = category.options.find(
    (o) => o.enumValue === parsed.enumValue && o.variant === parsed.variant,
  );
  return {
    encodedType: category.encodedMessageContent,
    messageContent: category.messageContent,
    messageValue: parsed.enumValue,
    deviceType: LiftType.ELEVATOR,
    label: option ? `${category.label}::${option.label}` : "",
  };
}

function bigintToUuidT(num: bigint): UuidT {
  const hex = num.toString(16).padStart(32, "0");
  const uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20, 32)}`;
  return stringToUuidT(uuid);
}

/** Convert arbitrary input (UUID string, numeric string, number, existing UuidT) to UuidT */
function toUuidT(val: any): UuidT {
  if (!val) return stringToUuidT("");
  if (typeof val === "string") {
    const stripped = val.replace(/-/g, "");
    if (stripped.length === 32 && /^[0-9a-fA-F]+$/.test(stripped)) {
      return stringToUuidT(val);
    }
    try {
      return bigintToUuidT(BigInt(val));
    } catch {
      return stringToUuidT("");
    }
  }
  if (typeof val === "number" || typeof val === "bigint") {
    return bigintToUuidT(BigInt(val));
  }
  if (val.value && (val.value.upper !== undefined || val.value.lower !== undefined)) {
    return val as UuidT;
  }
  return stringToUuidT("");
}

/** Scope options for a point within a bank */
type PointScope = "all_lifts" | "bank" | string[];

interface WizardPoint {
  name: string;
  pointId: number;
  msgRef: FbMessageRef | null;
  scope: PointScope;
  /** Raw ln2_msg preserved from existing CDO data (VTS format) when msgRef is unavailable */
  rawLn2Msg?: any;
}

interface BacnetWizardEditorProps {
  bankIds: string[];
  bankNames: Record<string, string>;
  liftNamesByBank: Record<string, Record<string, string>>;
  liftIdsByBank: Record<string, string[]>;
  messageFilter: Set<string> | undefined;
  cdoValue: AdapterBacnetInterface[];
  setCdo: React.Dispatch<React.SetStateAction<AdapterBacnetInterface[]>>;
  bankMappings: BacnetBankMapping[];
  setBankMappings: React.Dispatch<React.SetStateAction<BacnetBankMapping[]>>;
  siteName: string;
  setSiteName: (v: string) => void;
  siteId: string;
  setSiteId: (v: string) => void;
}

export default function BacnetWizardEditor({
  bankIds,
  bankNames,
  liftNamesByBank,
  liftIdsByBank,
  messageFilter,
  cdoValue,
  setCdo,
  bankMappings,
  setBankMappings,
  siteName,
  setSiteName,
  siteId,
  setSiteId,
}: BacnetWizardEditorProps) {
  const { theme } = useSelector((state: RootState) => state.home);

  // Per-bank points being configured
  const [pointsByBank, setPointsByBank] = useState<Record<string, WizardPoint[]>>({});

  // Draft state for BACnet ID text inputs (free-form editing, committed on blur/enter)
  const [bacnetBankIdDrafts, setBacnetBankIdDrafts] = useState<Record<number, string>>({});
  const [bacnetLiftIdDrafts, setBacnetLiftIdDrafts] = useState<Record<string, string>>({});

  // Track whether points were hydrated from existing CDO (prevents re-hydration on edits)
  const [hydrated, setHydrated] = useState(false);

  // Derive FlatbufferMessagePicker categoryFilter (category-level) and messageValueFilter
  // (per-message) from the adapter's messageFilter set ("categoryKey:enumValue" entries).
  const pickerCategoryFilter = React.useMemo<number[] | undefined>(() => {
    if (!messageFilter || messageFilter.size === 0) return undefined;
    const seen = new Set<number>();
    messageFilter.forEach((entry) => {
      const colonIdx = entry.indexOf(":");
      if (colonIdx < 0) return;
      const categoryKey = entry.slice(0, colonIdx);
      const mc = categoryKeyToMessageContent(categoryKey);
      if (mc !== 0) seen.add(mc);
    });
    return seen.size > 0 ? Array.from(seen) : undefined;
  }, [messageFilter]);

  // Per-message filter: Set<"messageContent:messageValue"> for the picker to restrict
  // individual options to only those present in the adapter's keys.
  const pickerMessageValueFilter = React.useMemo<Set<string> | undefined>(() => {
    if (!messageFilter || messageFilter.size === 0) return undefined;
    const out = new Set<string>();
    messageFilter.forEach((entry) => {
      const colonIdx = entry.indexOf(":");
      if (colonIdx < 0) return;
      const categoryKey = entry.slice(0, colonIdx);
      const mv = entry.slice(colonIdx + 1);
      const mc = categoryKeyToMessageContent(categoryKey);
      if (mc !== 0) out.add(`${mc}:${mv}`);
    });
    return out.size > 0 ? out : undefined;
  }, [messageFilter]);

  // ============================================
  // Hydrate WizardPoints from existing CDO data
  // ============================================

  /**
   * Try to extract an FbMessageRef from a CDO ln2_msg entry.
   * Handles three formats:
   * 1. Wizard format: msg has msg_id field directly
   * 2. Flat interface format: msg has message_content + message_value
   * 3. VTS FlatBuffer JSON: nested Type/Value pairs from Go Object API serialization
   *
   * VTS format structure (Go FlatBuffer *T types → JSON):
   *   ln2_msg.msg = ValuedMessageT { msg: MessageT, active: bool }
   *   MessageT = { content: EncodedMessageContentT { Type: 1|2, Value: OneHot|BitMask } }
   *   OneHot/BitMask = { message: MessageContentT { Type: 1-9, Value: CategoryMessageT } }
   *   CategoryMessageT has one union field → DeviceMessageT → VariantT → { message: enum }
   *   The recursive walkUnionToLeaf navigates all union levels generically.
   */
  /**
   * Extract an FbMessageRef from a CDO ln2_msg entry.
   * Handles three formats:
   * 1. Flat interface format: msg has encoded_type + message_content + message_value
   * 2. VTS FlatBuffer JSON: nested Type/Value pairs from Go Object API serialization
   * 3. Wizard format: msg has msg_id field (legacy — returns null, handled by rawLn2Msg)
   */
  const extractMsgRef = (ln2Msg: any): FbMessageRef | null => {
    if (!ln2Msg?.msg) return null;
    const msg = ln2Msg.msg;
    // Flat interface format: { encoded_type, message_content, message_value, fbs_device_type, ... }
    if (typeof msg.message_content === "number" && typeof msg.message_value === "number") {
      const mc = msg.message_content;
      const mv = msg.message_value;
      const et = msg.encoded_type ?? 0;
      const dt = msg.fbs_device_type ?? LiftType.ELEVATOR;
      if (mc === 0 && mv === 0) return null;
      return {
        encodedType: et,
        messageContent: mc,
        messageValue: mv,
        deviceType: dt as LiftType,
        label: "", // Will be resolved by the picker
      };
    }
    // VTS FlatBuffer JSON format: nested Type/Value union envelopes
    const content = msg?.msg?.content;
    if (content && (typeof content.Type === "number" || typeof content.Type === "string")) {
      const encodedType = typeof content.Type === "number" ? content.Type : 1;
      const messageUnion = content.Value?.message;
      if (
        messageUnion &&
        (typeof messageUnion.Type === "number" || typeof messageUnion.Type === "string")
      ) {
        const mcNum = resolveUnionType(messageUnion.Type);
        const leaf = walkUnionToLeaf(messageUnion.Value);
        if (leaf) {
          return {
            encodedType,
            messageContent: mcNum,
            messageValue: leaf.enumValue,
            deviceType: LiftType.ELEVATOR,
            label: "", // Will be resolved by the picker
          };
        }
      }
    }
    return null;
  };

  /**
   * Resolve a CDO bank_id (string, UuidT-like, or u128 object) to a wizard bankId (LN2 UUID string)
   * by looking up the BACnet→LN2 mapping.
   */
  const resolveBankId = (
    cdoBankId: any,
    mappings: BacnetBankMapping[],
    currentBankNames?: Record<string, string>,
  ): string | null => {
    if (!cdoBankId) return null;
    const cdoStr = typeof cdoBankId === "string" ? cdoBankId : "";
    const match = mappings.find((bm) => {
      const ln2Id = uuidToString(bm.ln2_bank_id);
      return (cdoStr && cdoStr === uuidToString(bm.bacnet_bank_id)) || (cdoStr && cdoStr === ln2Id);
    });
    if (match) {
      const matchedId = uuidToString(match.ln2_bank_id);
      // If no bank-names context or the ID is valid in the current org, return it.
      if (!currentBankNames || matchedId in currentBankNames) return matchedId;
      // The matched ID is not in the current org — it's a stale UUID from a
      // previous import. Fall through to name-based lookup below.
    }

    // UUID lookup failed or returned a stale id — the mapping may reference a
    // ln2_bank_id from a different import. Fall back to matching by bank name so
    // points survive org re-imports where bank UUIDs change but names stay the same.
    if (!currentBankNames || !cdoStr) return null;
    const staleMapping = match ?? mappings.find((bm) => uuidToString(bm.ln2_bank_id) === cdoStr);
    if (!staleMapping?.ln2_bank_name) return null;
    const newBankId = Object.entries(currentBankNames).find(
      ([, name]) => name === staleMapping.ln2_bank_name,
    )?.[0];
    return newBankId ?? null;
  };

  /**
   * Resolve a CDO lift_id to an LN2 lift UUID string using bank mapping.
   */
  const resolveLiftId = (cdoLiftId: any, bm: BacnetBankMapping): string | null => {
    if (!cdoLiftId) return null;
    const cdoStr = typeof cdoLiftId === "string" ? cdoLiftId : "";
    const match = bm.lift_mappings.find(
      (lm) =>
        (cdoStr && cdoStr === uuidToString(lm.bacnet_lift_id)) ||
        (cdoStr && cdoStr === uuidToString(lm.ln2_lift_id)),
    );
    if (!match) return null;
    return uuidToString(match.ln2_lift_id);
  };

  // Hydrate pointsByBank from existing cdoValue when it has data and we haven't hydrated yet
  useEffect(() => {
    if (hydrated) return;
    if (bankMappings.length === 0 || bankIds.length === 0) return;
    // Check if cdoValue has any descriptions
    const hasDescriptions = cdoValue.some((item) =>
      item.entries?.some((ent) => ent.descriptions?.length > 0),
    );
    if (!hasDescriptions) return;

    const newPoints: Record<string, WizardPoint[]> = {};
    let extractedSiteName = "";
    let extractedSiteId = "";

    // Build a reverse lookup: LN2 bank UUID → index in bankIds
    const ln2BankIdSet = new Set(bankIds);

    cdoValue.forEach((item) => {
      (item.entries || []).forEach((ent) => {
        // Resolve which LN2 bank this entry belongs to
        let ln2BankId = resolveBankId(ent.bank_id, bankMappings, bankNames);

        // If we couldn't resolve via mapping, try matching by bank index
        if (!ln2BankId) {
          // Try to find from the first description's ln2_msg.bank_id
          const firstDesc = ent.descriptions?.[0];
          if (firstDesc?.ln2_msg?.bank_id && ln2BankIdSet.has(firstDesc.ln2_msg.bank_id)) {
            ln2BankId = firstDesc.ln2_msg.bank_id;
          }
        }
        if (!ln2BankId || !ln2BankIdSet.has(ln2BankId)) return;

        // Find the bank mapping that owns this entry.
        // Search by ent.bank_id first (may be old ln2 UUID or bacnet UUID), then by
        // the resolved new UUID. This covers three cases:
        //   1. Same-org: ent.bank_id === bm.ln2_bank_id (current UUID)
        //   2. Re-import: ent.bank_id === bm.ln2_bank_id (old UUID — stale)
        //   3. CDO-page: ent.bank_id === bm.bacnet_bank_id (bacnet UUID)
        const cdoBankStr = typeof ent.bank_id === "string" ? ent.bank_id : "";
        const bm =
          bankMappings.find(
            (m) =>
              uuidToString(m.ln2_bank_id) === cdoBankStr ||
              uuidToString(m.bacnet_bank_id) === cdoBankStr,
          ) ?? bankMappings.find((m) => uuidToString(m.ln2_bank_id) === ln2BankId);

        // The render iterates bankMappings and uses uuidToString(bm.ln2_bank_id) as
        // the key into pointsByBank. Use that same key here so the lookup succeeds.
        const pointsKey = bm ? uuidToString(bm.ln2_bank_id) : ln2BankId;

        const allLiftIds = liftIdsByBank[ln2BankId] || [];
        const allLiftIdSet = new Set(allLiftIds);

        // Group descriptions by pointId to collapse per-lift duplicates
        const pointGroups = new Map<
          number,
          {
            name: string;
            msgRef: FbMessageRef | null;
            rawLn2Msg: any;
            liftIds: Set<string>;
            hasNoLift: boolean;
          }
        >();

        (ent.descriptions || []).forEach((desc: any) => {
          const pt = desc.point || {};
          const pointId = typeof pt.point_id === "number" ? pt.point_id : 0;
          const pointName = pt.point_name || "";
          const msgRef = extractMsgRef(desc.ln2_msg);

          // Extract site info from first point
          if (!extractedSiteName && pt.host_name) extractedSiteName = pt.host_name;
          if (!extractedSiteId && pt.host_id) {
            extractedSiteId = typeof pt.host_id === "string" ? pt.host_id : "";
          }

          // Determine which LN2 lift this description maps to
          let liftId: string | null = null;
          if (desc.ln2_msg?.lift_id && allLiftIdSet.has(desc.ln2_msg.lift_id)) {
            liftId = desc.ln2_msg.lift_id;
          } else if (bm && pt.lift_id) {
            liftId = resolveLiftId(pt.lift_id, bm);
          }

          const existing = pointGroups.get(pointId);
          if (existing) {
            if (liftId) existing.liftIds.add(liftId);
            else existing.hasNoLift = true;
          } else {
            const liftIds = new Set<string>();
            let hasNoLift = false;
            if (liftId) liftIds.add(liftId);
            else hasNoLift = true;
            pointGroups.set(pointId, {
              name: pointName,
              msgRef,
              rawLn2Msg: !msgRef ? desc.ln2_msg?.msg : undefined,
              liftIds,
              hasNoLift,
            });
          }
        });

        // Convert groups to WizardPoints with scope detection
        const points: WizardPoint[] = [];
        pointGroups.forEach((group, pointId) => {
          let scope: PointScope;
          if (group.hasNoLift && group.liftIds.size === 0) {
            scope = "bank";
          } else if (allLiftIds.length > 0 && group.liftIds.size >= allLiftIds.length) {
            scope = "all_lifts";
          } else if (group.liftIds.size > 0) {
            scope = Array.from(group.liftIds);
          } else {
            scope = "all_lifts";
          }
          points.push({
            name: group.name,
            pointId,
            msgRef: group.msgRef,
            scope,
            rawLn2Msg: group.rawLn2Msg,
          });
        });

        // Sort by pointId for consistent display
        points.sort((a, b) => a.pointId - b.pointId);

        if (points.length > 0) {
          newPoints[pointsKey] = points;
        }
      });
    });

    if (Object.keys(newPoints).length > 0) {
      setPointsByBank(newPoints);
      if (extractedSiteName && !siteName) setSiteName(extractedSiteName);
      if (extractedSiteId && !siteId) setSiteId(extractedSiteId);
      setHydrated(true);
    }
  }, [cdoValue, bankMappings, bankIds, hydrated]);

  // Auto-populate bank mappings from wizard state when empty
  useEffect(() => {
    if (bankMappings.length > 0 || bankIds.length === 0) return;

    const newMappings: BacnetBankMapping[] = bankIds.map((bankId, i) => {
      const liftIds = liftIdsByBank[bankId] || [];
      const names = liftNamesByBank[bankId] || {};
      return {
        bacnet_bank_id: bigintToUuidT(BigInt(i)),
        bacnet_bank_name: bankNames[bankId] || bankId,
        ln2_bank_id: stringToUuidT(bankId),
        ln2_bank_name: bankNames[bankId] || bankId,
        lift_mappings: liftIds.map((liftId, j) => ({
          bacnet_lift_id: bigintToUuidT(BigInt(j)),
          bacnet_lift_name: names[liftId] || liftId,
          ln2_lift_id: stringToUuidT(liftId),
          ln2_lift_name: names[liftId] || liftId,
        })),
      };
    });
    setBankMappings(newMappings);
  }, [bankIds, bankMappings.length]);

  // ============================================
  // Bank mapping edit helpers
  // ============================================
  const cloneMappings = (prev: BacnetBankMapping[]): BacnetBankMapping[] =>
    prev.map((bm) => ({
      ...bm,
      lift_mappings: bm.lift_mappings.map((lm) => ({ ...lm })),
    }));

  const updateBankMappingName = (bankIdx: number, value: string) => {
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIdx].bacnet_bank_name = value;
      return copy;
    });
  };

  const onChangeBacnetBankId = (bankIdx: number, value: string) => {
    setBacnetBankIdDrafts((prev) => ({ ...prev, [bankIdx]: value }));
  };

  const commitBacnetBankId = (bankIdx: number) => {
    const raw = bacnetBankIdDrafts[bankIdx];
    if (raw === undefined) return;
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIdx].bacnet_bank_id = toUuidT(raw);
      return copy;
    });
  };

  const updateLiftMappingName = (bankIdx: number, liftIdx: number, value: string) => {
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIdx].lift_mappings[liftIdx].bacnet_lift_name = value;
      return copy;
    });
  };

  const onChangeBacnetLiftId = (bankIdx: number, liftIdx: number, value: string) => {
    setBacnetLiftIdDrafts((prev) => ({
      ...prev,
      [`${bankIdx}-${liftIdx}`]: value,
    }));
  };

  const commitBacnetLiftId = (bankIdx: number, liftIdx: number) => {
    const key = `${bankIdx}-${liftIdx}`;
    const raw = bacnetLiftIdDrafts[key];
    if (raw === undefined) return;
    setBankMappings((prev) => {
      const copy = cloneMappings(prev);
      copy[bankIdx].lift_mappings[liftIdx].bacnet_lift_id = toUuidT(raw);
      return copy;
    });
  };

  // ============================================
  // Points management
  // ============================================
  const addPoint = (bankId: string) => {
    setPointsByBank((prev) => {
      const existing = prev[bankId] || [];
      // Next pointId: max existing + 1, or 0 if none
      const nextId = existing.length > 0 ? Math.max(...existing.map((p) => p.pointId)) + 1 : 0;
      return {
        ...prev,
        [bankId]: [...existing, { name: "", pointId: nextId, msgRef: null, scope: "all_lifts" }],
      };
    });
  };

  /** Extract the human-readable message label from a FbMessageRef */
  const msgLabel = (ref: FbMessageRef | null): string => ref?.label || "";

  const updatePoint = (bankId: string, pointIdx: number, updates: Partial<WizardPoint>) => {
    setPointsByBank((prev) => {
      const pts = [...(prev[bankId] || [])];
      const current = pts[pointIdx];
      const merged = { ...current, ...updates };

      // Auto-fill point name when msgRef changes, if the user hasn't customized it
      if (updates.msgRef !== undefined && updates.msgRef !== current.msgRef) {
        const prevAutoName = msgLabel(current.msgRef);
        if (current.name === "" || current.name === prevAutoName) {
          merged.name = msgLabel(updates.msgRef);
        }
        // Clear rawLn2Msg when user explicitly selects a new msgRef
        if (updates.msgRef) {
          merged.rawLn2Msg = undefined;
        }
      }

      pts[pointIdx] = merged;
      return { ...prev, [bankId]: pts };
    });
  };

  const removePoint = (bankId: string, pointIdx: number) => {
    setPointsByBank((prev) => {
      const pts = (prev[bankId] || []).filter((_, i) => i !== pointIdx);
      return { ...prev, [bankId]: pts };
    });
  };

  // ============================================
  // Build CDO from mappings + points
  // ============================================
  const buildCdoFromState = (): AdapterBacnetInterface[] => {
    const entries: AdapterBacnetInterface[] = [];

    bankMappings.forEach((bm, bankIdx) => {
      const bankId = bankIds[bankIdx];
      if (!bankId) return;
      const points = pointsByBank[bankId] || [];
      const descriptions: BacnetLn2Mapping[] = [];

      points.forEach((pt) => {
        // Skip points with neither msgRef nor rawLn2Msg
        if (!pt.msgRef && !pt.rawLn2Msg) return;

        // Build the msg payload: prefer msgRef (FbMessageRef), fall back to rawLn2Msg (VTS format)
        const msgPayload = pt.msgRef
          ? {
              encoded_type: pt.msgRef.encodedType,
              message_content: pt.msgRef.messageContent,
              message_value: pt.msgRef.messageValue,
              fbs_device_type: pt.msgRef.deviceType,
              position: pt.pointId,
              active: true,
            }
          : pt.rawLn2Msg;

        if (pt.scope === "bank") {
          // Bank-level point — no specific lift
          descriptions.push({
            point: {
              host_name: siteName,
              point_name: pt.name,
              group_name: bankNames[bankId] || "",
              host_id: siteId,
              group_id: uuidToString(bm.bacnet_bank_id),
              value: 0,
              point_id: pt.pointId,
              bank_name: bm.bacnet_bank_name,
              bank_id: uuidToString(bm.bacnet_bank_id),
              lift_name: "",
              lift_id: "",
            },
            ln2_msg: {
              bank_id: uuidToString(bm.ln2_bank_id),
              lift_id: "",
              msg: msgPayload,
            },
          });
        } else {
          // Expand to lifts
          const targetLifts =
            pt.scope === "all_lifts"
              ? bm.lift_mappings
              : bm.lift_mappings.filter((lm) => {
                  const liftId = uuidToString(lm.ln2_lift_id);
                  return (pt.scope as string[]).includes(liftId);
                });

          targetLifts.forEach((lm) => {
            descriptions.push({
              point: {
                host_name: siteName,
                point_name: pt.name,
                group_name: bankNames[bankId] || "",
                host_id: siteId,
                group_id: uuidToString(bm.bacnet_bank_id),
                value: 0,
                point_id: pt.pointId,
                bank_name: bm.bacnet_bank_name,
                bank_id: uuidToString(bm.bacnet_bank_id),
                lift_name: lm.bacnet_lift_name,
                lift_id: uuidToString(lm.bacnet_lift_id),
              },
              ln2_msg: {
                bank_id: uuidToString(bm.ln2_bank_id),
                lift_id: uuidToString(lm.ln2_lift_id),
                msg: structuredClone(msgPayload),
              },
            });
          });
        }
      });

      if (descriptions.length > 0) {
        entries.push({
          entries: [
            {
              descriptions,
              bank: {},
              bank_id: uuidToString(bm.bacnet_bank_id),
            },
          ],
        });
      }
    });

    return entries;
  };

  // Sync CDO whenever points or mappings change
  useEffect(() => {
    const hasPoints = Object.values(pointsByBank).some((pts) => pts.length > 0);
    if (hasPoints) {
      setCdo(buildCdoFromState());
    }
  }, [pointsByBank, bankMappings, siteName, siteId]);

  // ============================================
  // JSON Upload
  // ============================================
  const handleUploadJson = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json";
    input.onchange = (event: any) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const data = JSON.parse(text);

          // Try simplified upload format
          if (data && data.banks && Array.isArray(data.banks)) {
            processSimplifiedUpload(data as BacnetUploadFormat);
            return;
          }

          // Try full format with bacnet_bank_mappings
          if (data && data.bacnet_bank_mappings) {
            let parsedMappings: BacnetBankMapping[] = [];
            if (Array.isArray(data.bacnet_bank_mappings)) {
              parsedMappings = deserializeMappings(data.bacnet_bank_mappings);
              setBankMappings(parsedMappings);
            }
            if (Array.isArray(data.bacnet_config)) {
              setCdo(data.bacnet_config);
            }
            if (data.site_name) setSiteName(data.site_name);
            if (data.site_id) setSiteId(data.site_id);
            return;
          }

          message.error("Unrecognized JSON format");
        } catch {
          message.error("Failed to parse JSON file");
        } finally {
          input.value = "";
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const processSimplifiedUpload = (upload: BacnetUploadFormat) => {
    if (upload.site_name) setSiteName(upload.site_name);
    if (upload.site_id) setSiteId(upload.site_id);

    // Reverse lookup: bank name → bank ID
    const bankNameToId: Record<string, string> = {};
    Object.entries(bankNames).forEach(([id, name]) => {
      bankNameToId[name] = id;
    });

    const newMappings: BacnetBankMapping[] = [];
    const newPoints: Record<string, WizardPoint[]> = {};

    upload.banks.forEach((uploadBank, bankIdx) => {
      const bankId = bankNameToId[uploadBank.ln2_bank_name];
      if (!bankId) {
        message.warning(`Bank "${uploadBank.ln2_bank_name}" not found in wizard state — skipping`);
        return;
      }

      const liftIds = liftIdsByBank[bankId] || [];
      const names = liftNamesByBank[bankId] || {};

      // Reverse lookup: lift name → lift ID within this bank
      const liftNameToId: Record<string, string> = {};
      Object.entries(names).forEach(([id, name]) => {
        liftNameToId[name] = id;
      });

      // Build lift mappings from upload or default from wizard state
      const liftMappings =
        uploadBank.lifts && uploadBank.lifts.length > 0
          ? uploadBank.lifts.map((ul, j) => {
              const liftId = liftNameToId[ul.ln2_lift_name] || "";
              return {
                bacnet_lift_id: ul.bacnet_id
                  ? stringToUuidT(ul.bacnet_id)
                  : bigintToUuidT(BigInt(j)),
                bacnet_lift_name: ul.bacnet_name || ul.ln2_lift_name,
                ln2_lift_id: stringToUuidT(liftId),
                ln2_lift_name: ul.ln2_lift_name,
              };
            })
          : liftIds.map((liftId, j) => ({
              bacnet_lift_id: bigintToUuidT(BigInt(j)),
              bacnet_lift_name: names[liftId] || liftId,
              ln2_lift_id: stringToUuidT(liftId),
              ln2_lift_name: names[liftId] || liftId,
            }));

      newMappings.push({
        bacnet_bank_id: uploadBank.bacnet_id
          ? stringToUuidT(uploadBank.bacnet_id)
          : bigintToUuidT(BigInt(bankIdx)),
        bacnet_bank_name: uploadBank.bacnet_name || uploadBank.ln2_bank_name,
        ln2_bank_id: stringToUuidT(bankId),
        ln2_bank_name: uploadBank.ln2_bank_name,
        lift_mappings: liftMappings,
      });

      // Convert upload points
      if (uploadBank.points) {
        newPoints[bankId] = uploadBank.points.map((pt, ptIdx) => ({
          name: pt.name,
          pointId: pt.id != null ? Number(pt.id) : ptIdx,
          msgRef: compositeKeyToMsgRef(pt.msg_id),
          scope: pt.scope,
        }));
      }
    });

    if (newMappings.length > 0) {
      setBankMappings(newMappings);
    }
    setPointsByBank(newPoints);
  };

  // ============================================
  // JSON Download
  // ============================================
  const handleDownloadJson = () => {
    const output = {
      bacnet_bank_mappings: serializeMappings(bankMappings),
      bacnet_config: cdoValue,
      site_name: siteName,
      site_id: siteId,
    };
    const blob = new Blob([JSON.stringify(output, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bacnet-config.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ============================================
  // Scope select options helper
  // ============================================
  const getScopeOptions = (bankId: string) => {
    const liftIds = liftIdsByBank[bankId] || [];
    const names = liftNamesByBank[bankId] || {};
    return [
      { label: "All lifts", value: "all_lifts" },
      { label: "Bank only", value: "bank" },
      ...liftIds.map((id) => ({
        label: names[id] || id,
        value: id,
      })),
    ];
  };

  const getScopeValue = (scope: PointScope): string[] => {
    if (scope === "all_lifts") return ["all_lifts"];
    if (scope === "bank") return ["bank"];
    return scope;
  };

  const handleScopeChange = (bankId: string, pointIdx: number, value: string[]) => {
    const prev = getScopeValue((pointsByBank[bankId] || [])[pointIdx]?.scope ?? "all_lifts");

    // Detect newly added values
    const added = value.filter((v) => !prev.includes(v));

    // If a meta option was just selected, it wins exclusively
    if (added.includes("all_lifts")) {
      updatePoint(bankId, pointIdx, { scope: "all_lifts" });
      return;
    }
    if (added.includes("bank")) {
      updatePoint(bankId, pointIdx, { scope: "bank" });
      return;
    }

    // A specific lift was added — strip any meta options
    const liftIds = value.filter((v) => v !== "all_lifts" && v !== "bank");
    if (liftIds.length === 0) {
      updatePoint(bankId, pointIdx, { scope: "all_lifts" });
    } else {
      updatePoint(bankId, pointIdx, { scope: liftIds });
    }
  };

  // ============================================
  // Render
  // ============================================
  return (
    <div>
      {/* Site Info */}
      <DrawerRowDivider icon={faFileCode} title="BACnet Site" />
      <div className="container">
        <div className="row">
          <div className="col-12 col-md-6 mt-3">
            <span className="generalStyles__inputLabel">Site Name</span>
            <Input
              style={{ width: "100%", marginTop: 4 }}
              value={siteName}
              placeholder="Building name"
              onChange={(e) => setSiteName(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-6 mt-3">
            <span className="generalStyles__inputLabel">Site ID</span>
            <Input
              style={{ width: "100%", marginTop: 4 }}
              value={siteId}
              placeholder="Site identifier"
              onChange={(e) => setSiteId(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Bank Panels */}
      <div style={{ marginTop: 16 }}>
        <Collapse defaultActiveKey={bankIds.map((_, i) => String(i))} style={{ marginTop: 8 }}>
          {bankMappings.map((bm, bankIdx) => {
            const bankId = uuidToString(bm.ln2_bank_id);
            const points = pointsByBank[bankId] || [];

            return (
              <Collapse.Panel
                key={String(bankIdx)}
                header={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {bm.bacnet_bank_name || bankNames[bankId] || bankId}
                    </span>
                    <Tag>{bm.lift_mappings.length} lifts</Tag>
                    <Tag color="blue">
                      {points.length} point{points.length !== 1 ? "s" : ""}
                    </Tag>
                  </div>
                }
              >
                {/* Bank BACnet Name */}
                <div className="container">
                  <div className="row">
                    <div className="col-12 col-md-6 mt-2">
                      <span className="generalStyles__inputLabel">BACnet Bank Name</span>
                      <Input
                        size="small"
                        style={{ width: "100%", marginTop: 4 }}
                        value={bm.bacnet_bank_name}
                        onChange={(e) => updateBankMappingName(bankIdx, e.target.value)}
                      />
                    </div>
                    <div className="col-12 col-md-6 mt-2">
                      <span className="generalStyles__inputLabel">BACnet Bank ID</span>
                      <Input
                        size="small"
                        style={{ width: "100%", marginTop: 4 }}
                        value={bacnetBankIdDrafts[bankIdx] ?? uuidToString(bm.bacnet_bank_id)}
                        placeholder="UUID or number"
                        onChange={(e) => onChangeBacnetBankId(bankIdx, e.target.value)}
                        onBlur={() => commitBacnetBankId(bankIdx)}
                        onPressEnter={() => commitBacnetBankId(bankIdx)}
                      />
                    </div>
                  </div>
                </div>

                {/* Lift Mappings */}
                <div className="container" style={{ marginTop: 12 }}>
                  <span className="generalStyles__inputLabel" style={{ fontWeight: 500 }}>
                    Lift Mappings
                  </span>
                  {bm.lift_mappings.map((lm, liftIdx) => (
                    <div
                      key={liftIdx}
                      className="row"
                      style={{
                        marginTop: 4,
                        paddingLeft: 12,
                        borderLeft: "2px solid #333",
                        marginBottom: 4,
                      }}
                    >
                      <div className="col-6 col-md-4 mt-1">
                        <span className="generalStyles__inputLabel" style={{ fontSize: 11 }}>
                          LN2 Lift
                        </span>
                        <div style={{ fontSize: 12 }}>{lm.ln2_lift_name}</div>
                      </div>
                      <div className="col-6 col-md-4 mt-1">
                        <span className="generalStyles__inputLabel" style={{ fontSize: 11 }}>
                          BACnet Name
                        </span>
                        <Input
                          size="small"
                          value={lm.bacnet_lift_name}
                          onChange={(e) => updateLiftMappingName(bankIdx, liftIdx, e.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-4 mt-1">
                        <span className="generalStyles__inputLabel" style={{ fontSize: 11 }}>
                          BACnet Lift ID
                        </span>
                        <Input
                          size="small"
                          value={
                            bacnetLiftIdDrafts[`${bankIdx}-${liftIdx}`] ??
                            uuidToString(lm.bacnet_lift_id)
                          }
                          placeholder="UUID or number"
                          onChange={(e) => onChangeBacnetLiftId(bankIdx, liftIdx, e.target.value)}
                          onBlur={() => commitBacnetLiftId(bankIdx, liftIdx)}
                          onPressEnter={() => commitBacnetLiftId(bankIdx, liftIdx)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Points */}
                <div className="container" style={{ marginTop: 16 }}>
                  <span className="generalStyles__inputLabel" style={{ fontWeight: 500 }}>
                    Points
                  </span>
                  {points.map((pt, ptIdx) => (
                    <div
                      key={ptIdx}
                      className={`drawer__box__${theme}`}
                      style={{
                        padding: 10,
                        marginTop: 8,
                        borderRadius: 4,
                      }}
                    >
                      <div className="row">
                        <div className="col-12 col-md-3 mt-1">
                          <span className="generalStyles__inputLabel">Point Name</span>
                          <Input
                            size="small"
                            style={{ width: "100%", marginTop: 2 }}
                            value={pt.name}
                            placeholder="e.g. Position"
                            onChange={(e) =>
                              updatePoint(bankId, ptIdx, {
                                name: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="col-4 col-md-1 mt-1">
                          <span className="generalStyles__inputLabel">Point ID</span>
                          <InputNumber
                            size="small"
                            style={{ width: "100%", marginTop: 2 }}
                            min={0}
                            value={pt.pointId}
                            onChange={(v) =>
                              updatePoint(bankId, ptIdx, {
                                pointId: v ?? 0,
                              })
                            }
                          />
                        </div>
                        <div className="col-12 col-md-4 mt-1">
                          <span className="generalStyles__inputLabel">LN2 Message</span>
                          {pt.rawLn2Msg && !pt.msgRef && (
                            <Tag color="purple" style={{ marginLeft: 8, fontSize: 11 }}>
                              Custom mapping
                            </Tag>
                          )}
                          <FlatbufferMessagePicker
                            value={pt.msgRef}
                            onChange={(ref) => updatePoint(bankId, ptIdx, { msgRef: ref })}
                            categoryFilter={pickerCategoryFilter}
                            messageValueFilter={pickerMessageValueFilter}
                            allowClear
                          />
                        </div>
                        <div className="col-8 col-md-3 mt-1">
                          <span className="generalStyles__inputLabel">Scope</span>
                          <Select
                            size="small"
                            style={{ width: "100%", marginTop: 2 }}
                            mode="multiple"
                            value={getScopeValue(pt.scope)}
                            onChange={(v: string[]) => handleScopeChange(bankId, ptIdx, v)}
                            options={getScopeOptions(bankId)}
                          />
                        </div>
                        <div
                          className="col-2 col-md-1 mt-1"
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                          }}
                        >
                          <Tooltip title="Remove point">
                            <Button
                              size="small"
                              danger
                              shape="circle"
                              icon={<FontAwesomeIcon icon={faTrash} />}
                              onClick={() => removePoint(bankId, ptIdx)}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    size="small"
                    style={{ marginTop: 8 }}
                    onClick={() => addPoint(bankId)}
                    block
                  >
                    <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                    Add Point
                  </Button>
                </div>
              </Collapse.Panel>
            );
          })}
        </Collapse>
      </div>

      {/* Upload / Download */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 16,
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={handleUploadJson}>
          <FontAwesomeIcon icon={faFileCode} className="generalStyles__mrFix" />
          Upload JSON
        </Button>
        <Button onClick={handleDownloadJson}>
          <FontAwesomeIcon icon={faDownload} className="generalStyles__mrFix" />
          Download JSON
        </Button>
      </div>
    </div>
  );
}
